import {BehaviorSubject} from 'rxjs';
import {UntilDestroy} from '@ngneat/until-destroy';
import {Injectable} from '@angular/core';
import {FaultPatch} from '@docma-mm-core/fault/patch/fault-patch';
import {FaultPatchService, patchesByFUid} from '@docma-mm-core/fault/patch/fault-patch-service';
import {FaultPersistenceService} from '@docma-mm-core/fault/fault-persistence.service';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {FaultSyncQueuedItem} from '@docma-mm-core/fault/sync/fault-sync-queued-item';
import {Fault} from '@docma-mm-core/fault/fault';
import {TranslateService} from '@ngx-translate/core';
import {MmFaultsLoaderService} from './mm-faults-loader.service';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {ErrorService} from './mm-error.service';


@UntilDestroy()
@Injectable()
export class CurrentFaultsSyncStateService {

  currentSyncStateSubject = new BehaviorSubject<{
    countNotSynced: number,
    countWithSyncError: number,
    isSyncing: boolean,
    syncState: {[fUid: string]: {fault: Fault, lastAttemptOn: Date, error: string}},
    errorPatches: FaultPatch[]}>(null);

  currentSyncState$ = this.currentSyncStateSubject.asObservable();
  isSyncing: boolean;

  public faultsLoaderService: MmFaultsLoaderService;


  constructor(private readonly patchService: FaultPatchService,
              private readonly faultPersistenceService: FaultPersistenceService,
              private readonly mmPersonalizationService: PersonalizationService,
              private readonly translationService: TranslateService,
              private readonly errorService: ErrorService) {
  }

  async set(myDocmaUid: string, isSyncing: boolean = undefined) {
    if (!myDocmaUid){
      return;
    }

    if (isSyncing !== undefined && this.isSyncing !== isSyncing){
      this.isSyncing = isSyncing;
    }

    const syncResult: {items: FaultSyncQueuedItem[], lastAttemptOn: Date, error: string}[] = await this.mmPersonalizationService.getProjectValue(myDocmaUid, PersonalizationKeysEnum.SyncResult);
    let countWithSyncError = 0;
    let countNotSynced = 0;

    const allPatches: FaultPatch[] = await this.patchService.getAll(myDocmaUid);

    const groupedPatchesByFUid = patchesByFUid(allPatches);
    // const groupedPatchesByGUid = patchesByGUid(allPatches);

    const allFaults = await this.faultPersistenceService.getAll(myDocmaUid, Object.keys(groupedPatchesByFUid));
    const errorGroups: {[gUid: string]: {error: string, fuid: string}} = {};

    // for (let guid of Object.keys(groupedPatchesByGUid)) {
    //   groupedPatchesByGUid[guid].forEach(fp => {
    //     if (guid && !errorGroups.hasOwnProperty(guid)) {
    //       groupedPatchesByGUid[guid].forEach(g => {
    //         if (!errorGroups.hasOwnProperty(g.gUid)) {
    //           const fault = allFaults.find(x => x.uid === g.fUid);
    //           if (fault?.syncState?.status == SyncStatusEnum.Error) {
    //             // errorGroups[g.gUid] = {error: fault?.syncState.error, fuid: fault.uid};
    //           }
    //         }
    //       });
    //     }
    //   });
    // }

    const syncState: {[fUid: string]: {fault: Fault, lastAttemptOn: Date, error: string}} = {};
    const errorPatches: {[id: string]: FaultPatch} = {};

    const allFaultsDict: {[uid: string]: Fault} = {};
    const missingFaultsDict: {[uid: string]: Fault} = {};
    await allFaults.map(async x => allFaultsDict[x.uid] = x);

    const missingFaults = Object.keys(groupedPatchesByFUid).filter(fUid => !allFaultsDict.hasOwnProperty(fUid));

    await Promise.all(
    missingFaults
      .map(async fuid => {
        /* TRY RELOAD FAULT */
        // const loadedFaults = await firstValueFrom(this.faultsLoaderService.loadFaults(missingFaults));

        const patches = groupedPatchesByFUid[fuid];
        const f = new Fault({uid: fuid});
        f.syncState = new SyncState({ status: SyncStatusEnum.Error, error: `Fault with uniqueId ${fuid} couldn't be synced. (Inconsistend indexDb)` });
        // await this.faultPersistenceService.save(myDocmaUid, f);

        allFaults.push(f);
        missingFaultsDict[fuid] = f;
      }));



    for (let fuid of Object.keys(groupedPatchesByFUid)) {

      // const errorSync = syncResult?.find(s => s.error && s.items.findIndex(x => x.faultUid == fuid) !== -1);
      // const sync = syncResult?.find(s => !s.error && s.items.findIndex(x => x.faultUid == fuid) !== -1);

      let fault = allFaults.find(x => x.uid === fuid);
      groupedPatchesByFUid[fuid].sort((a, b) => { return a.date.getTime() - b.date.getTime()}).forEach((patch) => {
        const patchedValues = patch.operation?.value;
        if(patchedValues.Number2 && fault?.data !== null && fault?.data !== undefined) fault.data.Number2 = patchedValues.Number2;
        if(patchedValues.Desc1_1 && fault?.data !== null && fault?.data !== undefined) fault.data.Desc1_1 = patchedValues.Desc1_1;
      })

      const errorGroup = groupedPatchesByFUid[fuid].find(x => errorGroups.hasOwnProperty(x.gUid));
      const isErrorGroup = (errorGroup ? true : false);

      const errorMessage = ((errorGroup?.gUid && errorGroups[errorGroup.gUid] ? this.errorService.getErrorMessage(errorGroups[errorGroup.gUid]) : this.errorService.getErrorMessage(fault?.syncState.error)) ?? this.translationService.instant('MM.syncFaults.dialog.undefinedSyncError'));


      if (fault?.syncState.status == SyncStatusEnum.Error
          || isErrorGroup
          || fault?.attachments?.some(att => att.syncState.status == SyncStatusEnum.Error)
          || fault?.activities?.some(att => att.syncState.status == SyncStatusEnum.Error)) {

        syncState[fuid] = {fault, lastAttemptOn: fault.syncState.date, error: errorMessage};

        groupedPatchesByFUid[fuid].forEach(p => {
          const isErrorOp = syncResult?.some(x => x.items.some(f => f.id === p.operation.id))
            || (missingFaultsDict.hasOwnProperty(p.fUid) && missingFaultsDict[p.fUid].syncState.status === SyncStatusEnum.Error);

          if (!errorPatches.hasOwnProperty(p.operation.id) && isErrorOp){
            errorPatches[p.operation.id] = p;
          }
        });
        countWithSyncError++;
      }
      else {
        syncState[fuid] = {fault, lastAttemptOn: fault?.syncState.date, error: undefined};
        countNotSynced++;
      }
    }

    console.warn(`CURRENT SYNC STATE: not synced: ${countNotSynced}, errors: ${countWithSyncError}, isSyncing: ${this.isSyncing}`);

    this.currentSyncStateSubject.next({countNotSynced, countWithSyncError, isSyncing: this.isSyncing, syncState, errorPatches: Object.values(errorPatches)});
  }





}
